import { CheckCircleOutlined, CrownOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { closeVersionTrialReminder, getVersionTrialCurrent } from '@/pages/VersionTrial/service';
import { MenuDataTyping } from '@/services/userInterface';
import { findLockedMenuByPath } from '@/util/utils';
import { history, useLocation, useModel } from '@umijs/max';
import { Button, Checkbox, Modal, Space, Typography } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { canUseCustomerVersionTrialApi } from '@/utils/versionTrialContext';
import { BaseRouteUrl } from '../../../config/BASEURLCONFIG';

const { Paragraph, Text, Title } = Typography;

// 使用新版本 key，明确废弃旧企业级关闭记录和旧浏览器中的历史勾选状态。
const STORAGE_KEY_PREFIX = 'edatlink_upgrade_prompt_never_remind_v2';
const TRIAL_EXPIRE_STORAGE_KEY_PREFIX = 'edatlink_trial_expire_prompt_shown';
const FEATURE_LIST = [
  '保留免费版现有数据，升级后继续使用当前账号',
  '开通更多业务应用和管理能力',
  '可联系顾问确认功能范围和开通方式',
];

const isPreviewEnv = (envInfo?: { code?: string; enterpriseRoleCode?: string }) => {
  return envInfo?.code === 'PREVIEW_ENV' || envInfo?.enterpriseRoleCode === 'PREVIEW_ENV';
};

const isNoPromptPath = () => {
  const pathname = history.location.pathname || '';
  return (
    pathname.includes('/user/login') ||
    pathname.includes('/app/login') ||
    pathname.includes('/processEnv') ||
    pathname.includes('/prepareEnv') ||
    pathname.includes('/versionTrial')
  );
};

const UpgradePrompt: React.FC = () => {
  const { initialState } = useModel('@@initialState');
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [neverRemind, setNeverRemind] = useState(false);
  const [trialInfo, setTrialInfo] = useState<any>();
  const [loadedTrialInfoStorageKey, setLoadedTrialInfoStorageKey] = useState('');
  const [promptType, setPromptType] = useState<'upgrade' | 'trialExpire'>('upgrade');
  const [closedLockedPromptPath, setClosedLockedPromptPath] = useState('');

  const currentPath = BaseRouteUrl
    ? location.pathname.replace(BaseRouteUrl, '')
    : location.pathname;
  const lockedMenu = findLockedMenuByPath(
    (initialState?.menuData as MenuDataTyping[]) || [],
    currentPath,
  );
  const isLockedMenuPath = !!lockedMenu;
  const lockedPromptKey = lockedMenu?.path || currentPath;

  const userPromptIdentity = useMemo(() => {
    const currentUser = initialState?.currentUser;
    if (!currentUser) {
      return '';
    }

    const enterpriseIdentity = currentUser.enterpriseId || currentUser.enterpriseCode || 'unknownEnterprise';
    const userIdentity = currentUser.id || currentUser.mobile || currentUser.account || 'unknownUser';
    // 关闭提醒按企业+用户隔离，避免一个人关闭后影响同企业其他用户的本地提示。
    return `${enterpriseIdentity}_${userIdentity}`;
  }, [initialState?.currentUser]);

  const storageKey = userPromptIdentity ? `${STORAGE_KEY_PREFIX}_${userPromptIdentity}` : '';
  const trialExpireStorageKey = userPromptIdentity ? `${TRIAL_EXPIRE_STORAGE_KEY_PREFIX}_${userPromptIdentity}` : '';
  const trialInfoLoaded = !!storageKey && loadedTrialInfoStorageKey === storageKey;
  /*
   * 本地设置让当前设备立即生效，平台端设置保证同一账号换浏览器或设备后仍然生效。
   * 锁定菜单必须等 current 接口返回后再决定是否弹窗，避免页面刚打开时短暂误弹。
   */
  const upgradeReminderClosed =
    (storageKey && typeof window !== 'undefined'
      ? window.localStorage.getItem(storageKey) === 'Y'
      : false) ||
    (trialInfoLoaded && trialInfo?.trialReminderClosed === true);
  const lockedPromptOpen =
    isLockedMenuPath &&
    trialInfoLoaded &&
    !upgradeReminderClosed &&
    closedLockedPromptPath !== lockedPromptKey;
  const activePromptType = lockedPromptOpen ? 'upgrade' : promptType;
  const modalOpen = lockedPromptOpen || open;

  useEffect(() => {
    if (
      isMobile ||
      !storageKey ||
      !initialState?.currentUser ||
      isNoPromptPath() ||
      !canUseCustomerVersionTrialApi(initialState.currentUser, initialState.envInfo)
    ) {
      return;
    }

    const timer = window.setTimeout(async () => {
      let currentTrialInfo: any;
      try {
        // 弹窗展示前重新查询试用状态，避免只依赖启动时的 envInfo 缓存。
        currentTrialInfo = await getVersionTrialCurrent();
        setTrialInfo(currentTrialInfo);
      } catch (error) {
        console.log('getVersionTrialCurrent error', error);
      }

      // 即使接口失败也结束加载，锁定菜单仍可依据本地设置决定是否展示弹窗。
      setLoadedTrialInfoStorageKey(storageKey);

      // 锁定菜单的弹窗由 lockedPromptOpen 统一控制，避免和自动提醒同时打开。
      if (isLockedMenuPath) {
        return;
      }

      if (
        currentTrialInfo?.trialActive &&
        currentTrialInfo?.trialWillExpireSoon &&
        sessionStorage.getItem(trialExpireStorageKey) !== 'Y'
      ) {
        setPromptType('trialExpire');
        setOpen(true);
        return;
      }

      if (!isPreviewEnv(initialState?.envInfo) || localStorage.getItem(storageKey) === 'Y') {
        return;
      }

      if (currentTrialInfo?.trialReminderClosed || currentTrialInfo?.trialActive) {
        return;
      }

      setPromptType('upgrade');
      setOpen(true);
    }, 600);

    return () => window.clearTimeout(timer);
  }, [
    initialState?.currentUser,
    initialState?.envInfo,
    storageKey,
    trialExpireStorageKey,
    isLockedMenuPath,
  ]);

  const saveNeverRemind = async () => {
    if (activePromptType === 'upgrade' && neverRemind && storageKey) {
      localStorage.setItem(storageKey, 'Y');
      try {
        // 服务端按企业+用户保存，使当前账号在其他浏览器和设备上也不再收到申请试用提醒。
        await closeVersionTrialReminder();
      } catch (error) {
        // 平台端暂不可用时保留本地设置，不能因为同步失败让当前设备继续反复弹窗。
        console.log('closeVersionTrialReminder error', error);
      }
    }
  };

  const handleCancel = async () => {
    if (lockedPromptOpen) {
      // 锁定菜单弹窗和普通升级弹窗共用同一份“不再提醒”账号设置。
      await saveNeverRemind();
      setClosedLockedPromptPath(lockedPromptKey);
      setOpen(false);
      return;
    }

    if (activePromptType === 'trialExpire' && trialExpireStorageKey) {
      sessionStorage.setItem(trialExpireStorageKey, 'Y');
    }
    await saveNeverRemind();
    setOpen(false);
  };

  const handleUpgrade = async () => {
    if (lockedPromptOpen) {
      await saveNeverRemind();
      setClosedLockedPromptPath(lockedPromptKey);
      setOpen(false);
      history.push('/versionTrial');
      return;
    }

    if (activePromptType === 'trialExpire' && trialExpireStorageKey) {
      sessionStorage.setItem(trialExpireStorageKey, 'Y');
    }
    await saveNeverRemind();
    setOpen(false);
    history.push('/versionTrial');
  };

  const isTrialExpirePrompt = activePromptType === 'trialExpire';

  return (
    <Modal
      title={null}
      open={modalOpen}
      width={560}
      centered
      zIndex={lockedPromptOpen ? 2000 : undefined}
      destroyOnHidden
      maskClosable={false}
      footer={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            paddingTop: 4,
          }}
        >
          {isTrialExpirePrompt ? (
            <span />
          ) : (
            <Checkbox
              checked={neverRemind}
              onChange={(event) => setNeverRemind(event.target.checked)}
            >
              不再提醒
            </Checkbox>
          )}
          <Space>
            <Button onClick={handleCancel}>{isTrialExpirePrompt ? '稍后提醒' : '暂不升级'}</Button>
            <Button type="primary" onClick={handleUpgrade}>
              {isTrialExpirePrompt
                ? '查看开通方案'
                : trialInfo?.trialAlreadyUsed ? '查看升级方案' : '去申请试用15天'}
            </Button>
          </Space>
        </div>
      }
      onCancel={handleCancel}
    >
      <Space direction="vertical" size={18} style={{ width: '100%', padding: '6px 0 2px' }}>
        <div
          style={{
            display: 'flex',
            gap: 14,
            alignItems: 'flex-start',
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#eaf2ff',
              color: '#346afd',
              fontSize: 22,
              flex: '0 0 auto',
            }}
          >
            <CrownOutlined />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Space size={8} style={{ marginBottom: 8 }}>
              <Text type="secondary">当前版本</Text>
              <Text
                style={{
                  color: '#346afd',
                  background: '#eef4ff',
                  borderRadius: 4,
                  padding: '1px 8px',
                  fontSize: 12,
                }}
              >
                {isTrialExpirePrompt ? '尊享版试用中' : '免费版'}
              </Text>
            </Space>
            <Title level={4} style={{ margin: 0, lineHeight: 1.35 }}>
              {isTrialExpirePrompt
                ? '尊享版试用即将到期'
                : '升级尊享版，解锁更完整的生产管理能力'}
            </Title>
          </div>
        </div>

        <Paragraph style={{ marginBottom: 0, color: '#4b5563', lineHeight: 1.8 }}>
          {isTrialExpirePrompt
            ? `当前企业尊享版试用还剩 ${trialInfo?.trialRemainingDays || 0} 天。为避免试用到期影响正式使用，可以提前完成开通。`
            : '尊享版适合正式生产管理场景，支持生产、设备、安环等业务协同，便于企业从试用验证进入正式使用。'}
        </Paragraph>

        {!isTrialExpirePrompt && (
          <Space
            direction="vertical"
            size={10}
            style={{
              width: '100%',
              padding: '14px 16px',
              borderRadius: 8,
              background: '#f8fafc',
              border: '1px solid #edf1f7',
            }}
          >
            {FEATURE_LIST.map((item, index) => (
              <div
                key={item}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  color: '#1f2937',
                }}
              >
                {index === 2 ? (
                  <SafetyCertificateOutlined style={{ color: '#18a058', fontSize: 16 }} />
                ) : (
                  <CheckCircleOutlined style={{ color: '#18a058', fontSize: 16 }} />
                )}
                <Text>{item}</Text>
              </div>
            ))}
          </Space>
        )}
      </Space>
    </Modal>
  );
};

export default UpgradePrompt;
